/**
 * Hook to download the NYCBS Stat bulk upload template from Firebase Storage.
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import { getFileDownloadUrl } from "../../../../api/firebase/firestoreService";
import { ToastMessages } from "../../../../enums/toastMessages";
import { logError } from "../../../../utils/customLogger";
import { STAT_TEMPLATE_PATH } from "../constant/statTemplateFilePath";

/**
 * Provides a function and loading state for downloading the stat template CSV.
 *
 * @returns downloadTemplate - triggers the file download
 * @returns isDownloading - true while the download is in progress
 */
export const useStatTemplateDownload = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadTemplate = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const downloadUrl = await getFileDownloadUrl(STAT_TEMPLATE_PATH);

      if (!downloadUrl) {
        toast.error(ToastMessages.TEMPLATE_DOWNLOAD_ERROR);
        return;
      }

      const response = await fetch(downloadUrl);

      if (!response.ok) {
        toast.error(ToastMessages.TEMPLATE_DOWNLOAD_ERROR);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      const now = new Date();
      const timestamp =
        now.getFullYear().toString() +
        (now.getMonth() + 1).toString().padStart(2, "0") +
        now.getDate().toString().padStart(2, "0") +
        now.getHours().toString().padStart(2, "0") +
        now.getMinutes().toString().padStart(2, "0");
      link.download = `NYCBS_STAT_RisaHealth_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(ToastMessages.TEMPLATE_DOWNLOAD_SUCCESS);
    } catch (error) {
      logError(error as Error, "Error downloading stat template");
      toast.error(ToastMessages.TEMPLATE_DOWNLOAD_ERROR);
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    downloadTemplate,
    isDownloading,
  };
};
